import { useAuthStore } from "@/store/authStore";

const BASE = "/api/v1";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(`${BASE}${path}`, { ...init, headers, credentials: "include" });
}

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { access_token: string };
        useAuthStore.getState().setToken(data.access_token);
        return data.access_token;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await rawFetch(path, init);
  // 401 = expired/missing access token — try silent refresh once. 403 = role
  // failure; do NOT retry. We must allow refresh on /auth/me so the post-
  // reload bootstrap flow works; the only auth path we can't auto-refresh on
  // is /auth/refresh itself (would loop). Login/setup explicitly skip too —
  // a 401 there means bad creds, not an expired session.
  const noRetry =
    path === "/auth/refresh" ||
    path === "/auth/login" ||
    path === "/auth/logout" ||
    path === "/auth/setup" ||
    path === "/auth/setup-required";
  if (res.status === 401 && !noRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await rawFetch(path, init);
    }
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const detail =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = /filename="?([^";]+)"?/.exec(header);
  return m ? m[1] : null;
}

/**
 * Fetch a file (e.g. a CSV export) and trigger a browser download. Mirrors
 * apiFetch's auth + one-shot refresh handling, but returns the body as a blob
 * and saves it instead of parsing JSON. The download name comes from the
 * response's Content-Disposition, falling back to `fallbackName`.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  let res = await rawFetch(path, { method: "GET" });
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) res = await rawFetch(path, { method: "GET" });
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const detail =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, detail);
  }
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get("Content-Disposition")) ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
